import { useState, useEffect } from 'react';
import { adminManagementService } from '../services/adminManagementService';
import {
  SystemStats,
  UserInfo,
  CompanyInfo,
  PaginatedResponse,
  CompanyListResponse,
  UserSearchFilters,
  CompanySearchFilters,
  UpdateUserStatusRequest,
  CompanyApprovalRequest,
  AdminActionLog,
} from '../services/adminManagementService';

// 学生管理用フック
export const useAdminUsers = () => {
  const [users, setUsers] = useState<PaginatedResponse<UserInfo> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async (filters: UserSearchFilters = {}) => {
    try {
      setIsLoading(true);
      setError(null);
      const usersData = await adminManagementService.getUsers(filters);
      setUsers(usersData);
    } catch (err: any) {
      setError(err.response?.data?.error || '学生一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserStatus = async (
    userId: number,
    request: UpdateUserStatusRequest
  ) => {
    try {
      await adminManagementService.updateUserStatus(userId, request);
      // データを再取得
      await fetchUsers();
      return { success: true };
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || 'ユーザーステータスの更新に失敗しました';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return {
    users,
    isLoading,
    error,
    fetchUsers,
    updateUserStatus,
    refetch: () => fetchUsers(),
  };
};

// 学生詳細用フック
export const useAdminUserDetail = (userId: number | null) => {
  const [user, setUser] = useState<
    (UserInfo & { recent_activities: any[] }) | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserDetail = async (id: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const userData = await adminManagementService.getUserDetail(id);
      setUser(userData);
    } catch (err: any) {
      setError(err.response?.data?.error || '学生詳細の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserDetail(userId);
    }
  }, [userId]);

  return {
    user,
    isLoading,
    error,
    refetch: () => userId && fetchUserDetail(userId),
  };
};

// 企業管理用フック
export const useAdminCompanies = () => {
  const [companies, setCompanies] = useState<CompanyListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanies = async (filters: CompanySearchFilters = {}) => {
    try {
      setIsLoading(true);
      setError(null);
      const companiesData = await adminManagementService.getCompanies(filters);
      setCompanies(companiesData);
    } catch (err: any) {
      setError(err.response?.data?.error || '企業一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const updateCompanyApproval = async (
    companyId: number,
    request: CompanyApprovalRequest
  ) => {
    try {
      await adminManagementService.updateCompanyApproval(companyId, request);
      // データを再取得
      await fetchCompanies();
      return { success: true };
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || '企業承認処理に失敗しました';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  return {
    companies,
    isLoading,
    error,
    fetchCompanies,
    updateCompanyApproval,
    refetch: () => fetchCompanies(),
  };
};

// 承認待ち企業用フック
export const useAdminPendingCompanies = () => {
  const [pendingCompanies, setPendingCompanies] = useState<CompanyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPendingCompanies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const pendingData = await adminManagementService.getPendingCompanies();
      setPendingCompanies(pendingData);
    } catch (err: any) {
      setError(
        err.response?.data?.error || '承認待ち企業一覧の取得に失敗しました'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingCompanies();
  }, []);

  return {
    pendingCompanies,
    isLoading,
    error,
    refetch: fetchPendingCompanies,
  };
};

// 管理者アクションログ用フック
export const useAdminActionLogs = () => {
  const [logs, setLogs] = useState<PaginatedResponse<AdminActionLog> | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = async (page: number = 1, limit: number = 20) => {
    try {
      setIsLoading(true);
      setError(null);
      const logsData = await adminManagementService.getAdminActions(
        page,
        limit
      );
      setLogs(logsData);
    } catch (err: any) {
      setError(
        err.response?.data?.error || 'アクションログの取得に失敗しました'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  return {
    logs,
    isLoading,
    error,
    fetchLogs,
    refetch: () => fetchLogs(),
  };
};
